import { useState, useEffect, lazy, Suspense } from "react";
import useDelayedUnmount from "@/hooks/useDelayedUnmount";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar, MessageCircle, User, Megaphone, LogOut, Menu, X, Home, LifeBuoy, BarChart3, Bell, Wallet, UserPlus, Trash2, Gift, Wrench, FileText, Car, Store, BookOpen } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
// Lazy-loaded — the assistant dialog is 2400+ lines and pulls in doc / TTS
// / voice deps. Keeping it out of the initial bundle for every artisan
// page shaves a substantial chunk off first paint. Only downloaded when
// the pro actually opens the AI.
const ArtisanAssistantDialog = lazy(() => import("@/components/ArtisanAssistantDialog"));

import { YORM_LOGO_URL as LOGO_URL } from "@/lib/brand";
const APP_URL = typeof window !== 'undefined' ? window.location.origin : 'https://yorm.app';

export default function ArtisanHeader() {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [notifUnreadCount, setNotifUnreadCount] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);
  const [supportUnreadCount, setSupportUnreadCount] = useState(0);
  const [aiOpen, setAiOpen] = useState(false);
  const aiMounted = useDelayedUnmount(aiOpen);
  const [aiQuery, setAiQuery] = useState("");

  const openJobHelper = (q = "") => {
    setAiQuery(q);
    setAiOpen(true);
    setMobileMenuOpen(false);
  };

  // Consolidated header polling — one interval fires all three badges in
  // parallel and pauses when the tab is hidden. Cuts down from 3 separate
  // 30s intervals (which caused perceptible jank on mobile when they
  // fired back-to-back) to one visibility-gated 45s cycle.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const fetchAll = () => {
      Promise.allSettled([
        axios.get(`${API}/messages/unread-count?token=${token}`),
        axios.get(`${API}/support/unread-count?token=${token}`),
        axios.get(`${API}/user/notifications?token=${token}`),
      ]).then(([msg, sup, notif]) => {
        if (cancelled) return;
        if (msg.status === "fulfilled") setUnreadCount(msg.value.data?.count || 0);
        if (sup.status === "fulfilled") setSupportUnreadCount(sup.value.data?.unread_count || 0);
        if (notif.status === "fulfilled") {
          setNotifications(notif.value.data?.notifications || []);
          setNotifUnreadCount(notif.value.data?.unread_count || 0);
        }
      });
    };
    fetchAll();
    let iv = null;
    const start = () => {
      if (iv) return;
      iv = setInterval(fetchAll, 45000);
    };
    const stop = () => {
      if (iv) { clearInterval(iv); iv = null; }
    };
    const onVis = () => {
      if (document.hidden) stop();
      else { fetchAll(); start(); }
    };
    start();
    document.addEventListener("visibilitychange", onVis);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVis);
      stop();
    };
  }, [token]);

  const markAsRead = async (notifId, notif) => {
    try {
      await axios.post(`${API}/user/notifications/${notifId}/read?token=${token}`);
      setNotifications(prev => prev.map(n => n.id === notifId ? {...n, read: true} : n));
      setNotifUnreadCount(prev => Math.max(0, prev - 1));
      
      // Navigate based on notification type and data
      if (notif?.data?.booking_id) {
        navigate(`/artisan/chat/${notif.data.booking_id}?from=bell`);
      } else if (notif?.type === "booking" || notif?.type === "new_booking") {
        navigate("/professional/bookings");
      } else if (notif?.type === "message" || notif?.type === "new_message") {
        navigate("/professional/chats");
      }
    } catch (e) {
      // Ignore
    }
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/user/notifications/read-all?token=${token}`);
      setNotifications(prev => prev.map(n => ({...n, read: true})));
      setNotifUnreadCount(0);
      toast.success("All notifications marked as read");
    } catch (e) {
      toast.error("Failed to mark all as read");
    }
  };

  const deleteNotification = async (e, notifId) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/user/notifications/${notifId}?token=${token}`);
      const notif = notifications.find(n => n.id === notifId);
      setNotifications(prev => prev.filter(n => n.id !== notifId));
      if (notif && !notif.read) {
        setNotifUnreadCount(prev => Math.max(0, prev - 1));
      }
      toast.success("Notification cleared");
    } catch (e) {
      toast.error("Failed to clear notification");
    }
  };

  const deleteAllNotifications = async () => {
    if (!window.confirm("Clear all notifications?")) return;
    try {
      await axios.delete(`${API}/user/notifications?token=${token}`);
      setNotifications([]);
      setNotifUnreadCount(0);
      toast.success("All notifications cleared");
    } catch (e) {
      toast.error("Failed to clear notifications");
    }
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const handleInviteFriend = async () => {
    const shareData = {
      title: 'Join Yorm',
      text: 'Find trusted professionals near you! Download the Yorm app and book services anywhere, anytime.',
      url: APP_URL
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        if (err.name !== 'AbortError') {
          await navigator.clipboard.writeText(`${shareData.text} ${shareData.url}`);
          toast.success("Invite link copied to clipboard!");
        }
      }
    } else {
      await navigator.clipboard.writeText(`${shareData.text} ${shareData.url}`);
      toast.success("Invite link copied to clipboard!");
    }
  };

  const isActive = (path) => location.pathname === path;

  const navItems = [
    { path: "/professional", icon: Home, label: "Dashboard" },
    { path: "/professional/profile", icon: User, label: "Profile" },
    { path: "/professional/business", icon: Store, label: "My Business" },
    { path: "/professional/bookings", icon: Calendar, label: "Bookings" },
    { path: "/professional/invoices", icon: FileText, label: "Invoices & Docs" },
    { path: "/professional/chats", icon: MessageCircle, label: "Messages", badge: unreadCount },
    { path: "/professional/promotions", icon: Megaphone, label: "Promote" },
    { path: "/professional/vehicles", icon: Car, label: "Vehicles" },
    { path: "/professional/payments", icon: Wallet, label: "Payments" },
    { path: "/professional/analytics", icon: BarChart3, label: "Analytics" },
    { path: "/professional/support", icon: LifeBuoy, label: "Support", badge: supportUnreadCount, badgeColor: "red" },
    { path: "/professional/how-it-works", icon: BookOpen, label: "How it works" },
  ];

  return (
    <>
    <header
      className={`fixed top-0 left-0 right-0 ${mobileMenuOpen ? "z-[1150]" : "z-[1100]"} apple-bar apple-bar-top`}
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/professional" className="flex items-center gap-2 flex-shrink-0">
            <img src={LOGO_URL} alt="Yorm" className="h-9 w-9 object-contain" />
            <span className="text-xl text-[#222222] logo-text hidden sm:inline">Yorm</span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1 min-w-0">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  title={item.label}
                  aria-label={item.label}
                  className={`flex items-center gap-1.5 px-2.5 py-2 rounded-lg transition-colors relative text-sm whitespace-nowrap ${
                    isActive(item.path)
                      ? "bg-[#4F988E]/10 text-[#4F988E] font-medium"
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                  }`}
                >
                  <Icon size={16} className="flex-shrink-0" />
                  <span className={isActive(item.path) ? "" : "hidden"}>{item.label}</span>
                  {item.badge > 0 && (
                    <span className={`absolute -top-1 -right-1 min-w-[18px] h-[18px] text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 ${
                      item.badgeColor === 'red' ? 'bg-red-500 animate-pulse' : 'bg-red-500'
                    }`}>
                      {item.badge > 99 ? "99+" : item.badge}
                    </span>
                  )}
                </Link>
              );
            })}
            
            <div className="flex items-center gap-2 ml-2 pl-2 border-l border-gray-200">
              {/* Notifications Bell */}
              <DropdownMenu open={notifOpen} onOpenChange={setNotifOpen}>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant={notifUnreadCount > 0 ? "default" : "ghost"} 
                    size="sm" 
                    className={`relative ${
                      notifUnreadCount > 0 
                        ? "bg-[#D4A853] hover:bg-[#c49943] text-white animate-pulse" 
                        : ""
                    }`}
                    data-testid="artisan-notifications-btn"
                  >
                    <Bell size={18} />
                    {notifUnreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 border-2 border-white">
                        {notifUnreadCount > 99 ? "99+" : notifUnreadCount}
                      </span>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-96 flex flex-col" sideOffset={8}>
                  {/* Fixed Header */}
                  <div className="bg-white border-b px-4 py-3 flex-shrink-0">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-[#222222]">Notifications</h3>
                      <div className="flex items-center gap-2">
                        {notifUnreadCount > 0 && (
                          <button onClick={markAllRead} className="text-xs text-[#D4A853] hover:underline">
                            Mark all read
                          </button>
                        )}
                        {notifications.length > 0 && (
                          <button onClick={deleteAllNotifications} className="text-xs text-red-500 hover:underline flex items-center gap-1">
                            <Trash2 size={12} /> Clear all
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                  {/* Scrollable Content */}
                  <div className="overflow-y-auto max-h-[350px] flex-1">
                    {notifications.length === 0 ? (
                      <div className="py-8 text-center text-gray-500">
                        <Bell size={32} className="mx-auto mb-2 opacity-30" />
                        <p className="text-sm">No notifications yet</p>
                      </div>
                    ) : (
                      <div>
                        {notifications.slice(0, 20).map((notif) => (
                          <div
                            key={notif.id}
                            onClick={() => {
                              markAsRead(notif.id, notif);
                              setNotifOpen(false);
                              if (notif.type === "message" && notif.booking_id) {
                                navigate(`/artisan/chat/${notif.booking_id}?from=bell`);
                              } else if (notif.booking_id) {
                                navigate(`/artisan/chat/${notif.booking_id}?from=bell`);
                              } else {
                                navigate("/professional/notifications");
                              }
                            }}
                            className={`relative px-4 py-3 border-b cursor-pointer hover:bg-gray-50 transition ${
                              !notif.read ? "bg-amber-50" : ""
                            }`}
                          >
                            <div className="flex items-start gap-3 pr-8">
                              <div className={`p-2 rounded-full flex-shrink-0 ${
                                notif.type === "message" ? "bg-green-100 text-green-600" :
                                notif.type === "approval" ? "bg-green-100 text-green-600" :
                                notif.type === "booking" ? "bg-blue-100 text-blue-600" :
                                "bg-gray-100 text-gray-600"
                              }`}>
                                <Bell size={16} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <p className="text-sm font-medium text-[#222222]">{notif.title}</p>
                                  {!notif.read && (
                                    <span className="w-2 h-2 bg-[#D4A853] rounded-full flex-shrink-0" />
                                  )}
                                </div>
                                <p className="text-xs text-[#717171] line-clamp-2">{notif.message}</p>
                                <p className="text-xs text-gray-400 mt-1">{formatTimeAgo(notif.created_at)}</p>
                              </div>
                            </div>
                            <button
                              onClick={(e) => deleteNotification(e, notif.id)}
                              className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-red-100 text-gray-400 hover:text-red-500 transition"
                              title="Clear notification"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {/* Fixed Footer */}
                  {notifications.length > 0 && (
                    <div className="bg-white border-t flex-shrink-0">
                      <Link
                        to="/professional/notifications"
                        onClick={() => setNotifOpen(false)}
                        className="block px-4 py-3 text-center text-sm text-[#D4A853] font-medium hover:bg-gray-50"
                      >
                        View All Notifications
                      </Link>
                    </div>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Yorm Ai Job Helper — text hides below xl so 10 nav items
                  + notifications + avatar all fit on 1366 px laptops. */}
              <button
                onClick={() => openJobHelper()}
                className="inline-flex items-center justify-center gap-1.5 px-3 xl:px-4 py-2 bg-gradient-to-r from-[#4F988E] to-[#3d7a72] text-white text-sm font-medium rounded-full hover:opacity-90 transition whitespace-nowrap"
                data-testid="artisan-job-helper-btn"
                title="Job Helper AI"
              >
                <Wrench size={14} className="flex-shrink-0" />
                <span className="hidden xl:inline">Job Helper AI</span>
              </button>

              {/* Avatar dropdown — bundles Profile, Refer & Earn and
                  Logout into a single compact popover so the logout is
                  always visible even on 1024–1366 px laptop screens. */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="flex items-center gap-1.5 rounded-full p-1 hover:bg-gray-100 transition"
                    data-testid="artisan-avatar-menu"
                    title="Account menu"
                  >
                    <div className="w-8 h-8 bg-[#4F988E] rounded-full flex items-center justify-center text-white text-sm font-medium">
                      {user?.full_name?.charAt(0)}
                    </div>
                    <span className="text-gray-700 text-sm font-medium hidden xl:inline max-w-[80px] truncate">{user?.full_name?.split(" ")[0]}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64" sideOffset={8}>
                  <div className="px-3 py-2.5 border-b">
                    <p className="text-sm font-semibold text-[#222222] truncate">{user?.full_name || "Professional"}</p>
                    {user?.email && <p className="text-xs text-gray-500 truncate">{user.email}</p>}
                  </div>
                  <DropdownMenuItem onClick={() => navigate("/professional/profile")} data-testid="artisan-menu-profile">
                    <User size={16} className="mr-2" /> Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/professional/referral")} data-testid="artisan-menu-referral">
                    <Gift size={16} className="mr-2 text-[#D4A853]" /> Refer & Earn
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600 focus:text-red-600 focus:bg-red-50" data-testid="artisan-menu-logout">
                    <LogOut size={16} className="mr-2" /> Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </nav>

          {/* Medium screen nav - Show fewer items */}
          <nav className="hidden md:flex lg:hidden items-center gap-1">
            {navItems.slice(0, 5).map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-1 px-2 py-2 rounded-lg transition-colors relative text-xs ${
                    isActive(item.path)
                      ? "bg-[#4F988E]/10 text-[#4F988E] font-medium"
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                  }`}
                >
                  <Icon size={16} />
                  <span>{item.label}</span>
                  {item.badge > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center px-0.5">
                      {item.badge > 99 ? "99+" : item.badge}
                    </span>
                  )}
                </Link>
              );
            })}
            
            <div className="flex items-center gap-1 ml-1 pl-1 border-l border-gray-200">
              {/* Notifications */}
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate("/professional/notifications")}
                className="relative"
              >
                <Bell size={18} />
                {notifUnreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                    {notifUnreadCount > 99 ? "99+" : notifUnreadCount}
                  </span>
                )}
              </Button>

              {/* User */}
              <div className="w-7 h-7 bg-[#4F988E] rounded-full flex items-center justify-center text-white text-xs font-medium">
                {user?.full_name?.charAt(0)}
              </div>

              {/* Logout */}
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleLogout}
                className="text-gray-500 hover:text-red-500"
              >
                <LogOut size={16} />
              </Button>
            </div>
          </nav>

          <button className="md:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="artisan-mobile-menu-toggle">
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Nav — full-viewport overlay so nothing shows behind it.
          High z-index so Leaflet map overlays (My location pill, city
          chips, geocoder) don't leak through when the menu is open. */}
      {mobileMenuOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/30 z-[9998] animate-in fade-in-0 duration-200"
            onClick={() => setMobileMenuOpen(false)}
            data-testid="artisan-mobile-menu-backdrop"
          />
          <div
            className="fixed top-14 sm:top-16 right-0 bottom-20 sm:bottom-16 w-1/3 min-w-[280px] max-w-[440px] bg-white shadow-2xl border-l border-gray-100 rounded-l-2xl px-4 py-4 space-y-2 overflow-y-auto z-[9999] animate-in slide-in-from-right duration-300"
            style={{ paddingTop: "max(1rem, env(safe-area-inset-top))" }}
            data-testid="artisan-mobile-menu"
          >
          {/* Close (back) button pinned to the top of the menu so the
              user always has a one-tap exit. */}
          <div className="flex items-center justify-between pb-2 mb-1 border-b border-gray-100">
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">Menu</span>
            <button
              type="button"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-1 text-sm text-[#4F988E] font-semibold hover:bg-[#4F988E]/10 rounded-full px-3 py-1.5 -mr-1"
              data-testid="artisan-mobile-menu-close"
              aria-label="Close menu"
            >
              <X size={18} /> Close
            </button>
          </div>
          {/* Yorm Ai Job Helper search bar */}
          {/* AI Job Helper moved to a dedicated tile on the pro
              dashboard — the search bar used to live here but crowded
              the right-side menu. The `openJobHelper` handler is kept
              elsewhere for the Job Helper AI button on desktop. */}

          {/* Notifications - not in footer */}
          <Link
            to="/professional/notifications"
            onClick={() => setMobileMenuOpen(false)}
            className={`flex items-center gap-3 px-3 py-3 rounded-lg ${
              isActive("/professional/notifications")
                ? "bg-[#D4A853]/10 text-[#D4A853] font-medium"
                : "text-gray-600"
            }`}
          >
            <Bell size={20} />
            <span>Notifications</span>
            {notifUnreadCount > 0 && (
              <span className="ml-auto min-w-[20px] h-[20px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
                {notifUnreadCount > 99 ? "99+" : notifUnreadCount}
              </span>
            )}
          </Link>
          {/* Profile - not in footer */}
          <Link
            to="/professional/profile"
            onClick={() => setMobileMenuOpen(false)}
            className={`flex items-center gap-3 px-3 py-3 rounded-lg ${
              isActive("/professional/profile")
                ? "bg-[#D4A853]/10 text-[#D4A853] font-medium"
                : "text-gray-600"
            }`}
          >
            <User size={20} />
            <span>Profile</span>
          </Link>
          <Link
            to="/professional/business"
            onClick={() => setMobileMenuOpen(false)}
            data-testid="artisan-mobile-menu-business"
            className={`flex items-center gap-3 px-3 py-3 rounded-lg ${
              isActive("/professional/business")
                ? "bg-[#D4A853]/10 text-[#D4A853] font-medium"
                : "text-gray-600"
            }`}
          >
            <Store size={20} />
            <span>My Business</span>
          </Link>
          {/* Payments - not in footer */}
          <Link
            to="/professional/payments"
            onClick={() => setMobileMenuOpen(false)}
            className={`flex items-center gap-3 px-3 py-3 rounded-lg ${
              isActive("/professional/payments")
                ? "bg-[#D4A853]/10 text-[#D4A853] font-medium"
                : "text-gray-600"
            }`}
          >
            <Wallet size={20} />
            <span>Payments</span>
          </Link>
          {/* Support - not in footer */}
          <Link
            to="/professional/support"
            onClick={() => setMobileMenuOpen(false)}
            className={`flex items-center gap-3 px-3 py-3 rounded-lg ${
              isActive("/professional/support")
                ? "bg-[#D4A853]/10 text-[#D4A853] font-medium"
                : "text-gray-600"
            }`}
          >
            <LifeBuoy size={20} />
            <span>Support</span>
            {supportUnreadCount > 0 && (
              <span className="ml-auto min-w-[20px] h-[20px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 animate-pulse">
                {supportUnreadCount > 99 ? "99+" : supportUnreadCount}
              </span>
            )}
          </Link>
          <Link
            to="/professional/how-it-works"
            onClick={() => setMobileMenuOpen(false)}
            data-testid="menu-how-it-works"
            className={`flex items-center gap-3 px-3 py-3 rounded-lg ${
              isActive("/professional/how-it-works")
                ? "bg-[#D4A853]/10 text-[#D4A853] font-medium"
                : "text-gray-600"
            }`}
          >
            <BookOpen size={20} />
            <span>How it works</span>
          </Link>
          {/* Refer & Earn - Mobile */}
          <button
            onClick={() => {
              setMobileMenuOpen(false);
              navigate("/professional/referral");
            }}
            className="flex items-center gap-3 px-3 py-3 rounded-lg text-[#D4A853] hover:bg-[#D4A853]/10 w-full"
          >
            <Gift size={20} />
            <span>Refer & Earn</span>
          </button>
          <Button variant="outline" className="w-full mt-4" onClick={handleLogout}>
            <LogOut size={18} className="mr-2" /> Logout
          </Button>
          </div>
        </>
      )}
    </header>

    {/* Yorm Ai Job Helper for artisans — lazy-loaded, only mounted when
        the pro actually opens it (aiOpen flips true) so the 2400-line
        module isn't pulled into the initial bundle. */}
    {aiMounted && (
      <Suspense fallback={null}>
        <ArtisanAssistantDialog open={aiOpen} onOpenChange={setAiOpen} initialQuery={aiQuery} />
      </Suspense>
    )}
    </>
  );
}
